import type { BootstrapColor } from '@/types/tailwind';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Protect, ProtectProps } from '../Protect';

interface CardProps {
  children: React.ReactNode;
  color?: BootstrapColor | 'default';
}

export function Card({ children, color }: CardProps) {
  return (
    <div className="mb-5 w-full">
      <div
        className={clsx(
          'rounded-lg border',
          {
            primary: 'bg-fiona-primary-background',
            secondary: 'bg-fiona-gray/50',
            success: 'bg-fiona-success-background',
            danger: 'bg-fiona-danger-background',
            warning: 'bg-fiona-warning-background',
            info: 'bg-fiona-info-background',
            default: 'bg-white',
          }[color ?? 'default'] satisfies Partial<Record<BootstrapColor | 'default', string>>,
        )}
      >
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// TODO: AEP-770 Replace controls with children
export function CardHeader({
  title,
  subTitle,
  controls,
}: {
  title?: string | React.ReactNode;
  subTitle?: string;
  controls?: React.ReactNode;
}) {
  return (
    <div
      className={clsx(
        'mb-4 border-b pb-3 sm:items-center',
        title && controls ? 'flex justify-between' : '',
      )}
    >
      {title && <CardHeading title={title} subTitle={subTitle} />}
      {controls && <div className="mt-4 sm:mt-0">{controls}</div>}
    </div>
  );
}

export function CardHeading({
  title,
  subTitle,
}: {
  title: string | React.ReactNode;
  subTitle?: string;
}) {
  return (
    <div className="flex flex-col">
      {typeof title === 'string' ? (
        <span className="text-fiona-highlight text-base font-bold">{title}</span>
      ) : (
        title
      )}
      {subTitle && <p className="mt-1 text-sm text-gray-500">{subTitle}</p>}
    </div>
  );
}

interface LeftNavContainerProps {
  groups: LeftNavLinkGroupProps[];
}

interface LeftNavLinkGroupProps {
  title: string;
  links: LeftNavLinkProps[];
  currentPath?: string;
}

interface LeftNavLinkProps {
  href: string;
  text: string;
  isActive?: boolean;
  protectWith?: ProtectProps['roles'];
}

export function LeftNavContainer({ groups }: LeftNavContainerProps) {
  const currentPath = usePathname();
  return (
    <div className=" sticky top-20 h-[90vh] w-60 rounded-lg border bg-white p-2">
      <nav className="mt-3 flex flex-1 flex-col gap-2">
        {groups.map(group => (
          <LeftNavLinkGroup
            key={group.title}
            title={group.title}
            links={group.links}
            currentPath={currentPath}
          />
        ))}
      </nav>
    </div>
  );
}

export function LeftNavLinkGroup({ title, links, currentPath = '' }: LeftNavLinkGroupProps) {
  return (
    <div className="flex flex-col">
      <p className="text-fiona-gray-2 ml-5 text-xs font-semibold uppercase">{title}</p>
      <ul role="list" className="mt-1.5 flex flex-1 flex-col gap-y-1 text-sm">
        {links.map(link => (
          <LeftNavLink
            key={link.href}
            href={link.href}
            text={link.text}
            isActive={currentPath === link.href || currentPath.startsWith(link.href + '/')}
            protectWith={link.protectWith}
          />
        ))}
      </ul>
    </div>
  );
}

export function LeftNavLink({ href, text, isActive, protectWith }: LeftNavLinkProps) {
  const linkElem = (
    <Link href={href}>
      {' '}
      <li
        className={clsx(
          'rounded-lg py-2 pl-5 font-semibold hover:cursor-pointer',
          isActive ? 'bg-indigo-700 text-white' : 'hover:bg-gray-50',
        )}
      >
        {text}
      </li>
    </Link>
  );
  const protectElem = protectWith ? <Protect roles={protectWith}>{linkElem}</Protect> : null;

  return <>{protectElem || linkElem}</>;
}
